import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Box, Button, Flex, Loading, Typography } from '@iheartjane/reefer';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import { useGetStore } from '@jane/shared/data-access';
import { useNavigateBack } from '@jane/shared/hooks';
import type { Store } from '@jane/shared/types';

import { Modal, QRCodeCallout } from '../../components';
import { kioskAuthenticationQueryKeys } from '../../data-access';
import type { RootLoaderType } from '../../data-access';
import {
  useKioskSpacing,
  useKioskUserSession,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '../../hooks';
import { useModalsDisplayedContext } from '../../providers';
import qrPng from '../assets/qr-logo.png';

const getSource = (pathname: string) => {
  if (pathname.includes('checkout')) return 'Checkout';
  if (pathname.includes('cart')) return 'Bag';
  return 'Menu';
};

export const Login = ({ fromCart }: { fromCart?: boolean }) => {
  const { pathname } = useLocation();
  const { defaultSpacing, getKioskSpacing, isIpad } = useKioskSpacing();
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { deviceGrant, userName, signedIn, isLoading, qrCodeError } =
    useKioskUserSession({
      store,
    });

  const goBack = useNavigateBack(`stores/${storeId}/menu`);
  const queryClient = useQueryClient();
  const { setLoginModalDisplayed } = useModalsDisplayedContext();

  const janeGold = useShouldKioskShowGold();
  const janePay = store?.jane_pay_integration?.enabled;
  const hideJanePayBranding = store?.jane_pay_integration?.white_label;
  const enableKioskQRCodeAuthWithExternalURL = useFlag(
    FLAGS.c3EnableKioskQRCodeAuthWithExternalURL
  );

  const onClose = () => {
    if (signedIn) {
      queryClient.invalidateQueries(kioskAuthenticationQueryKeys.kioskUser());
    }
    setLoginModalDisplayed(false);
    goBack();
  };

  const mobileLoginUrl = useMemo(() => {
    if (!deviceGrant) {
      return undefined;
    }

    const { hostname, port, protocol } = window.location;

    const source = getSource(pathname);

    if (
      enableKioskQRCodeAuthWithExternalURL &&
      deviceGrant.external_authentication_url != null
    ) {
      const externalUrl = new URL(deviceGrant.external_authentication_url);
      externalUrl.searchParams.set('user_code', deviceGrant.user_code);
      externalUrl.searchParams.set('source', source);

      return externalUrl.toString();
    }

    return `${protocol}//${hostname}${
      port ? `:${port}` : ''
    }/stores/${storeId}/mobile/login?user_code=${
      deviceGrant.user_code
    }&source=${source}`;
  }, [deviceGrant, pathname, storeId, enableKioskQRCodeAuthWithExternalURL]);

  useEffect(() => {
    setLoginModalDisplayed(true);
  }, []);

  return (
    <Modal
      appId="root"
      contentLabel="Login"
      onRequestClose={onClose}
      open
      overlayClose
      variant="dialogue"
    >
      <Modal.Content padding={false}>
        {isLoading ? (
          <Box position="relative" p={40}>
            <Loading />
          </Box>
        ) : signedIn ? (
          <Flex
            flexDirection="column"
            alignItems="center"
            m={getKioskSpacing(32, 40)}
            width="432px"
          >
            <Typography
              variant="title-bold"
              mb={getKioskSpacing(24, 8)}
              width="inherit"
            >
              {userName?.includes('privaterelay.appleid.com')
                ? 'Welcome'
                : `${userName}, welcome`}{' '}
              to the kiosk at {store.name}
            </Typography>
            {(janePay || janeGold) && (
              <Typography mb={defaultSpacing}>
                With your Jane account you can{' '}
                {janeGold && 'earn cash back with Jane Gold'}
                {janePay && janeGold && ' and'}
                {janePay &&
                  ` shop faster${
                    hideJanePayBranding ? '' : ' with Jane Pay'
                  } at this kiosk`}
                .
              </Typography>
            )}
            <Button
              label={`${fromCart ? 'Continue' : 'Start'} shopping`}
              onClick={onClose}
              full
              branded
              size={isIpad ? 'small' : 'default'}
            />
          </Flex>
        ) : (
          <QRCodeCallout
            buttonLabel="Go back"
            onClick={onClose}
            qrCode={qrPng}
            type="login"
            url={mobileLoginUrl}
            error={qrCodeError}
          />
        )}
      </Modal.Content>
    </Modal>
  );
};
